import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, useRef, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Dr Corassa - Cura pela Alimentação" },
      { name: "description", content: "Nutricionista Dr. Corassa" },
      { name: "author", content: "Lovable" },
      { property: "og:title", content: "Dr Corassa - Cura pela Alimentação" },
      { property: "og:description", content: "Nutricionista Dr. Corassa" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Lovable" },
      { name: "twitter:title", content: "Dr Corassa - Cura pela Alimentação" },
      { name: "twitter:description", content: "Nutricionista Dr. Corassa" },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/667d1473-7cc0-408b-8ac2-c4b4329e2862/id-preview-af712578--e0494bb4-679b-49cc-bb67-a16272adef1b.lovable.app-1781316854324.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/667d1473-7cc0-408b-8ac2-c4b4329e2862/id-preview-af712578--e0494bb4-679b-49cc-bb67-a16272adef1b.lovable.app-1781316854324.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://kegqh.ttrk.io" },
      { rel: "dns-prefetch", href: "https://kegqh.ttrk.io" },
      { rel: "preconnect", href: "https://connect.facebook.net" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

const META_PIXEL_ID = "733509557626024";
const metaPixelScript = `
!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];
t=b.createElement(e);t.async=!0;t.src=v;
s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s);
}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
try{window.__fbPageViewId=(window.crypto&&window.crypto.randomUUID)?window.crypto.randomUUID():String(Date.now())+Math.random().toString(16).slice(2);}catch(e){window.__fbPageViewId=String(Date.now());}
window.__fbPageViewTime=Math.floor(Date.now()/1000);
window.__fbPixelId='${META_PIXEL_ID}';
// Advanced Matching: reaproveita dados já informados pelo visitante
var __am={};try{
  var __id=JSON.parse(window.localStorage.getItem('fb_identity')||'{}');
  if(__id.email)__am.em=String(__id.email).trim().toLowerCase();
  if(__id.phone){var __d=String(__id.phone).replace(/\\D/g,'');if(__d){__am.ph=(__d.length===10||__d.length===11)?('55'+__d):__d;}}
  if(__id.firstName)__am.fn=String(__id.firstName).trim().toLowerCase();
  if(__id.lastName)__am.ln=String(__id.lastName).trim().toLowerCase();
  if(__id.gender)__am.ge=String(__id.gender).toLowerCase().charAt(0)==='f'?'f':'m';
}catch(e){}
// external_id estável criado já no 1o acesso (localStorage + cookie 1 ano)
try{
  var __ck=function(n){var m=document.cookie.match(new RegExp('(^| )'+n+'=([^;]+)'));return m?decodeURIComponent(m[2]):'';};
  var __eid='';
  try{__eid=window.localStorage.getItem('lvbl_eid')||'';}catch(e){}
  if(!__eid)__eid=__ck('lvbl_eid');
  if(!__eid){try{__eid=window.localStorage.getItem('external_id')||'';}catch(e){}}
  if(!__eid){__eid=(window.crypto&&window.crypto.randomUUID)?window.crypto.randomUUID():(String(Date.now())+Math.random().toString(36).slice(2));}
  try{window.localStorage.setItem('lvbl_eid',__eid);}catch(e){}
  document.cookie='lvbl_eid='+encodeURIComponent(__eid)+'; path=/; max-age=31536000; SameSite=Lax';
  __am.external_id=__eid;
  // _fbp de fallback caso o Pixel seja bloqueado
  var __fbp=__ck('_fbp');
  if(!/^fb\\.1\\.\\d+\\.\\d+$/.test(__fbp||'')){
    document.cookie='_fbp=fb.1.'+Date.now()+'.'+Math.floor(Math.random()*1e10)+'; path=/; max-age=7776000; SameSite=Lax';
  }
}catch(e){}
// _fbc: URL -> localStorage(fb_click) -> cookie, ANTES do init para o Pixel já anexar
try{
  var __q=new URLSearchParams(window.location.search);
  var __fbclid=__q.get('fbclid');
  var __urlFbc=__q.get('fbc');
  if(!__fbclid && __urlFbc && /^fb\\.1\\.\\d+\\..+$/.test(__urlFbc)){__fbclid=__urlFbc.split('.').slice(3).join('.');}
  if(__fbclid){try{window.localStorage.setItem('fb_click',JSON.stringify({fbclid:__fbclid,ts:Date.now()}));}catch(e){}}
  var __ts=Date.now();
  if(!__fbclid){
    try{var __st=JSON.parse(window.localStorage.getItem('fb_click')||'null');if(__st&&__st.fbclid){__fbclid=__st.fbclid;__ts=__st.ts||__ts;}}catch(e){}
  }
  var __cookieFbc=(document.cookie.match(/(^| )_fbc=([^;]+)/)||[])[2];
  if(__fbclid && (!__cookieFbc || decodeURIComponent(__cookieFbc).indexOf(__fbclid)===-1)){
    document.cookie='_fbc=fb.1.'+__ts+'.'+__fbclid+'; path=/; max-age=7776000; SameSite=Lax';
  }
  window.__fbHasClick=!!(__fbclid||__cookieFbc);
}catch(e){}
fbq('init', '${META_PIXEL_ID}', __am);
fbq('track', 'PageView', {}, { eventID: window.__fbPageViewId });
`;


function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]!) : null;
}

type PageViewWindow = Window & {
  __fbPageViewId?: string;
  __fbPageViewTime?: number;
  __fbPageViewSent?: boolean;
  __fbHasClick?: boolean;
  fbq?: (...args: unknown[]) => void;
  __fbPixelId?: string;
};

/** Envia o PageView para a CAPI com beacon + retry. Mesmo event_id do Pixel. */
async function postPageView(
  eventId: string,
  eventTime: number,
  useBeacon: boolean,
): Promise<void> {
  const { getVisitorIdentity, resolveFbClick, getOrCreateExternalId, ensureFbp } = await import(
    "@/lib/visitor-identity"
  );
  const identity = getVisitorIdentity();
  const click = resolveFbClick();
  const body = JSON.stringify({
    event_id: eventId,
    event_time: eventTime,
    fbp: getCookie("_fbp") ?? ensureFbp(),
    fbc: click.fbc,
    fbclid: click.fbclid,
    external_id: getOrCreateExternalId(),
    event_source_url: window.location.href,
    referrer: document.referrer || null,
    email: identity.email ?? null,
    phone: identity.phone ?? null,
    first_name: identity.firstName ?? null,
    last_name: identity.lastName ?? null,
    gender: identity.gender ?? null,
  });

  if (useBeacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      const ok = navigator.sendBeacon(
        "/api/public/pageview",
        new Blob([body], { type: "application/json" }),
      );
      if (ok) return;
    } catch {
      /* cai no fetch */
    }
  }

  // fetch com 2 novas tentativas — mesmo event_id, então nunca duplica na Meta.
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await fetch("/api/public/pageview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      });
      if (res.ok) return;
    } catch {
      /* tenta de novo */
    }
    if (attempt < 2) await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
  }
}

function MetaCapiPageView() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const firstPath = useRef<string | null>(null);

  // Carregamento inicial: dispara cedo, sem esperar 8s.
  useEffect(() => {
    const w = window as unknown as PageViewWindow;
    if (w.__fbPageViewSent) return;

    let cancelled = false;
    let timer = 0;

    const send = (useBeacon = false) => {
      if (w.__fbPageViewSent) return;
      w.__fbPageViewSent = true;
      const eventId = w.__fbPageViewId ?? String(Date.now());
      const eventTime = w.__fbPageViewTime ?? Math.floor(Date.now() / 1000);
      void postPageView(eventId, eventTime, useBeacon);
    };

    // Espera curta pelo _fbp (o _fbc já é resolvido de forma síncrona no head).
    const started = Date.now();
    const MAX_WAIT = 1200;
    const tick = () => {
      if (cancelled || w.__fbPageViewSent) return;
      if (getCookie("_fbp") || Date.now() - started >= MAX_WAIT) {
        send();
        return;
      }
      timer = window.setTimeout(tick, 150);
    };
    timer = window.setTimeout(tick, 100);

    // Se o visitante sair antes, garante a entrega via beacon.
    const flush = () => {
      if (document.visibilityState === "hidden") send(true);
    };
    const onPageHide = () => send(true);
    document.addEventListener("visibilitychange", flush);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", flush);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  // Navegação interna (SPA): novo event_id, Pixel + CAPI juntos.
  useEffect(() => {
    if (firstPath.current === null) {
      firstPath.current = pathname;
      return;
    }
    if (firstPath.current === pathname) return;
    firstPath.current = pathname;

    const w = window as unknown as PageViewWindow;
    const eventId =
      typeof window.crypto?.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const eventTime = Math.floor(Date.now() / 1000);
    w.__fbPageViewId = eventId;
    w.__fbPageViewTime = eventTime;
    try {
      w.fbq?.("track", "PageView", {}, { eventID: eventId });
    } catch {
      /* ignore */
    }
    void postPageView(eventId, eventTime, false);
  }, [pathname]);

  return null;
}




function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: metaPixelScript }} />
      </head>
      <body>
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
            alt=""
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <Toaster />
      <MetaCapiPageView />
    </QueryClientProvider>
  );
}
